import { GoogleMap } from "@js/google-maps.js";

const createMarkerElement = (logo) => {
    const element = document.createElement("div");
    element.className = "map-marker";

    if (logo) {
        const image = document.createElement("img");
        image.src = logo;
        image.alt = "";
        element.append(image);
    }

    return element;
};

// Google Maps injects its own Material Symbols stylesheet, which overrides the one of the theme.
const removeMapsSymbolsLink = () => {
    const links = document.querySelectorAll('link[href*="Material"]');
    if (links.length > 1) links[links.length - 1].remove();
};

const initMap = async (layout) => {
    const mapEl = layout.querySelector(".location-map[data-api-key]");
    const list = layout.querySelector(".location-list");
    if (!mapEl || !list) return;

    const cards = [...list.querySelectorAll("[data-location-id]")];
    const locations = JSON.parse(mapEl.dataset.locations || "[]");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    mapEl.classList.add("is-loading");

    const map = new GoogleMap({
        apiKey: mapEl.dataset.apiKey,
        mapId: mapEl.dataset.mapId,
        clustering: true,
    });

    try {
        await map.init(mapEl);
    } catch (error) {
        mapEl.classList.remove("is-loading");
        mapEl.classList.add("has-error");
        throw error;
    }

    let activeId = null;

    const highlight = (id, options = {}) => {
        map.setActive(id ? map.findMarker("id", id) : null, options);
    };

    const scrollToCard = (card) => {
        list.scrollTo({ top: card.offsetTop - list.offsetTop - 16, behavior: reduceMotion.matches ? "auto" : "smooth" });
    };

    const activate = (id, { fromMarker = false } = {}) => {
        activeId = id;
        highlight(id);

        cards.forEach((card) => {
            const isActive = Number(card.dataset.locationId) === id;
            card.classList.toggle("is-active", isActive);
            if (isActive && fromMarker) scrollToCard(card);
        });
    };

    map.addMarkers(
        locations.map((location) => ({
            position: { lat: location.lat, lng: location.lng },
            title: location.title,
            content: createMarkerElement(mapEl.dataset.marker),
            data: { id: location.id },
            onClick: () => activate(location.id, { fromMarker: true }),
        }))
    );

    google.maps.event.addListenerOnce(map.map, "tilesloaded", () => {
        mapEl.classList.remove("is-loading");
        removeMapsSymbolsLink();
    });

    cards.forEach((card) => {
        const id = Number(card.dataset.locationId);
        card.addEventListener("mouseenter", () => highlight(id, { pan: false }));
        card.addEventListener("mouseleave", () => highlight(activeId, { pan: false }));
        card.addEventListener("focusin", () => highlight(id));
        card.addEventListener("focusout", () => highlight(activeId, { pan: false }));
        card.addEventListener("click", () => activate(id));
    });
};

const initToggle = (layout) => {
    const toggle = layout.querySelector(".view-toggle-switch");
    if (!toggle) return;

    toggle.addEventListener("click", () => {
        const isList = toggle.getAttribute("aria-checked") !== "true";
        toggle.setAttribute("aria-checked", String(isList));
        layout.classList.toggle("is-list-view", isList);
    });
};

const initList = (layout) => {
    const tiles = layout.querySelector(".list-row .tiles");
    const nav = layout.querySelector(".list-row .pagination-col");
    const filters = [...layout.querySelectorAll(".list-row .filter-button")];
    if (!tiles || !layout.dataset.ajaxUrl) return;

    let region = "all";
    let request = null;

    const load = async (page, { scroll = false } = {}) => {
        request?.abort();
        request = new AbortController();
        tiles.setAttribute("aria-busy", "true");
        layout.classList.add("is-loading");

        const body = new URLSearchParams({
            action: "bz_location_map",
            post_id: layout.dataset.postId,
            selector: layout.dataset.selector,
            row: layout.dataset.row,
            layout: layout.dataset.layout,
            region,
            page: String(page),
        });

        try {
            const response = await fetch(layout.dataset.ajaxUrl, { method: "POST", body, signal: request.signal });
            const json = await response.json();
            if (!json.success) throw new Error("location_map");

            tiles.innerHTML = json.data.html;
            if (nav) {
                nav.innerHTML = json.data.pagination;
                nav.hidden = json.data.pages < 2;
            }

            if (scroll) {
                const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
                layout.querySelector(".list-row").scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
                tiles.querySelector(".locatie_tile-title a")?.focus({ preventScroll: true });
            }
        } catch (error) {
            if (error.name !== "AbortError") console.error("[location_map]", error);
        } finally {
            tiles.setAttribute("aria-busy", "false");
            layout.classList.remove("is-loading");
        }
    };

    filters.forEach((button) => {
        button.addEventListener("click", () => {
            if (button.dataset.region === region) return;
            region = button.dataset.region;
            filters.forEach((other) => {
                const active = other === button;
                other.classList.toggle("is-active", active);
                other.setAttribute("aria-pressed", String(active));
            });
            load(1);
        });
    });

    nav?.addEventListener("click", (event) => {
        const button = event.target.closest("button[data-page]");
        if (!button || button.disabled || button.classList.contains("is-active")) return;
        load(Number(button.dataset.page), { scroll: true });
    });
};

document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(".flex-layout.location_map-layout").forEach((layout) => {
        initToggle(layout);
        initList(layout);

        const mapEl = layout.querySelector(".location-map[data-api-key]");
        if (!mapEl) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return;
                observer.disconnect();
                initMap(layout).catch((error) => console.error("[location_map]", error));
            },
            { rootMargin: "300px" }
        );

        observer.observe(mapEl);
    });
});
